<template>
  <div
    class="floating-window"
    :style="windowStyle"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    v-if="list.isDisplay === '1'"
  >
    <a :href="list.jumpUrl" target="_blank">
      <img
        class="floating-window-icon"
        :src="list.backgroundImageUrl"
        style="border-radius: 6px"
      />
    </a>
  </div>
</template>

<script>
  /**
   * FloatingWindow 是一个可漂浮的窗口组件。
   * 它会在页面内随机移动，并支持自定义速度和边界偏移。
   *
   * 功能特点：
   * - 随机漂浮：窗口会在页面内以指定速度随机移动。
   * - 边界检测：窗口会根据配置的边界偏移值自动调整方向，避免超出容器范围。
   * - 鼠标交互：鼠标悬停时停止移动，鼠标移出后延迟 1 秒恢复移动。
   * - 点击事件：支持绑定点击事件处理函数。
   *
   * @example
   * ```vue
   * <FloatingWindow
   *   :speed="3"
   *   :offset="{ top: 50, right: 20, bottom: 20, left: 20 }"
   *   @click="handleClick"
   * >
   *   点我！
   * </FloatingWindow>
   * ```
   *
   * @props {Number} speed - 漂浮窗口的移动速度，默认值为 2。
   * @props {Object} offset - 边界偏移配置，默认值为 `{ top: 100, right: 20, bottom: 20, left: 20, width: 300, height: 300 }`。
   * 用于控制窗口与容器边界的最小距离以及窗口的宽高。
   *
   * @emits click - 当用户点击窗口时触发。
   */

  import { getFloatingWindow } from "@/api/home.js"
  export default {
    name: "FloatingWindow",
    props: {
      speed: {
        type: Number,
        default: 2
      },

      // 新增边界偏移配置
      offset: {
        type: Object,
        default: () => ({
          top: 90,
          right: 20,
          bottom: 20,
          left: 20,
          width: 300,
          height: 150
        })
      }
    },
    data() {
      return {
        x: 0,
        y: 0,

        angle: Math.random() * 360,
        containerWidth: window.innerWidth,
        containerHeight: window.innerHeight,
        // 新增数据属性，用于记录鼠标是否悬停
        hovered: false,
        hovertimer: 0,

        list: {
          id: null,
          backgroundImageUrl: "",
          jumpUrl: "",
          isDisplay: "0"
        }
      }
    },
    computed: {
      windowStyle() {
        const windowWidth = this.containerWidth

        // 移动端 固定显示
        if (windowWidth <= 768) {
          return {
            position: "static",
            "margin-top": "20px",
            width: "100%",
            height: "calc(50vw - 30px)"
          }
        }

        // pc端 飘动
        return {
          left: `${this.x}px`,
          top: `${this.y}px`
        }
      }
    },
    mounted() {
      this.move()
      window.addEventListener("resize", this.handleResize)

      this.load()
    },
    beforeDestroy() {
      clearInterval(this.timer)
      window.removeEventListener("resize", this.handleResize)
    },
    methods: {
      async load() {
        await this.request()
      },
      request() {
        return getFloatingWindow().then((res) => {
          const d = res.data.data || {}
          this.list = {
            ...this.list,
            ...d
          }
        })
      },
      move() {
        this.timer = setInterval(() => {
          if (this.hovered) return // 鼠标悬停时停止位移

          const radians = this.angle * (Math.PI / 180)
          const dx = this.speed * Math.cos(radians)
          const dy = this.speed * Math.sin(radians)

          this.x += dx
          this.y += dy

          // 调整边界检测逻辑
          if (this.x < this.offset.left) {
            this.angle = 180 - this.angle
            this.x = this.offset.left
          } else if (
            this.x + this.offset.width >
            this.containerWidth - this.offset.right
          ) {
            this.angle = 180 - this.angle
            this.x = this.containerWidth - this.offset.width - this.offset.right
          }

          if (this.y < this.offset.top) {
            this.angle = 360 - this.angle
            this.y = this.offset.top
          } else if (
            this.y + this.offset.height >
            this.containerHeight - this.offset.bottom
          ) {
            this.angle = 360 - this.angle
            this.y =
              this.containerHeight - this.offset.height - this.offset.bottom
          }
        }, 16) // 大约每秒60帧
      },
      handleResize() {
        this.containerWidth = window.innerWidth
        this.containerHeight = window.innerHeight
      },
      onMouseEnter() {
        this.hovered = true // 鼠标移入时设置为 true
      },
      onMouseLeave() {
        clearTimeout(this.hovertimer)
        // 延迟 1 秒后再恢复移动
        this.hovertimer = setTimeout(() => {
          this.hovered = false // 鼠标移出后恢复为 false
        }, 1000)
      }
    }
  }
</script>

<style scoped>
  .floating-window {
    position: fixed;
    min-width: 50px;
    min-height: 50px;
    z-index: 1500;
    cursor: pointer;

    width: 300px;
    height: 150px;
  }

  .floating-window img {
    width: 100%;
    height: 100%;
  }
</style>
